import { useState } from "react"
import { AlertTriangle, Clock, MessageSquareWarning, Smile, ThumbsDown, TrendingUp } from "lucide-react"
import type { Dashboard } from "../../shared/domain"
import { ErrorBox, HBars, Loading, MonthColumns, Panel, Empty, StatusBadge } from "@/components/common"
import { NativeSelect } from "@/components/ui/native-select"
import { useApp } from "@/lib/app-context"
import { fmtMonth, fmtNum, fmtShort, fmtSigned, useAsync } from "@/lib/format"
import { cn } from "@/lib/utils"

export function DashboardPage() {
  const { api, go } = useApp()
  const thisYear = new Date().getFullYear()
  const [year, setYear] = useState(thisYear)
  const { data, error, loading, reload } = useAsync(() => api.dashboard(year), [api, year])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">Painel da qualidade</h2>
          <p className="text-xs text-muted-foreground">Ano de {year}</p>
        </div>
        <NativeSelect className="w-28" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[thisYear, thisYear - 1, thisYear - 2].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </NativeSelect>
      </div>

      {error ? <ErrorBox message={error} onRetry={reload} /> : null}
      {loading && !data ? <Loading /> : null}
      {data ? <DashboardBody d={data} onOpen={(id) => go(`/reclamacoes/${id}`)} /> : null}
    </div>
  )
}

function Kpi({
  icon: Icon,
  label,
  value,
  unit,
  hint,
  tone,
}: {
  icon: typeof Smile
  label: string
  value: string
  unit?: string
  hint?: string
  tone?: "bad" | "good"
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
        {unit ? <span className="text-sm text-muted-foreground">{unit}</span> : null}
      </div>
      {hint ? (
        <p className={cn("mt-1 text-xs", tone === "bad" ? "text-red-600" : tone === "good" ? "text-green-700" : "text-muted-foreground")}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

function DashboardBody({ d, onOpen }: { d: Dashboard; onOpen: (id: string) => void }) {
  const k = d.kpis
  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi
          icon={Smile}
          label="Satisfação média"
          value={fmtNum(k.satisfaction_avg)}
          unit="/ 5"
          hint={k.satisfaction_avg == null ? "Sem respostas" : `Objetivo: ${fmtNum(d.goals.satisfaction_avg)}`}
          tone={k.satisfaction_avg != null && k.satisfaction_avg < d.goals.satisfaction_avg ? "bad" : undefined}
        />
        <Kpi
          icon={TrendingUp}
          label="NPS"
          value={fmtSigned(k.nps)}
          hint={k.nps == null ? "Sem respostas" : `${k.promoters_pct}% promotores · ${k.detractors_pct}% detratores`}
        />
        <Kpi
          icon={MessageSquareWarning}
          label="Taxa de resposta"
          value={k.response_rate == null ? "—" : `${k.response_rate}%`}
          hint={`${k.responses} respostas`}
        />
        <Kpi
          icon={AlertTriangle}
          label="Reclamações abertas"
          value={String(k.open)}
          hint={k.overdue ? `${k.overdue} com prazo ultrapassado` : "Nenhuma em atraso"}
          tone={k.overdue ? "bad" : "good"}
        />
        <Kpi
          icon={Clock}
          label="Tempo médio de fecho"
          value={k.avg_close_days == null ? "—" : String(k.avg_close_days)}
          unit="dias"
          hint={`Objetivo: até ${d.goals.close_days} dias`}
          tone={k.avg_close_days != null && k.avg_close_days > d.goals.close_days ? "bad" : undefined}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Reclamações por mês" className="lg:col-span-2">
          <MonthColumns items={d.by_month.map((m) => ({ label: fmtMonth(m.month), value: m.received, hint: `${m.closed} encerradas` }))} />
        </Panel>
        <Panel title="Reclamações por motivo">
          {d.by_category.length ? (
            <HBars items={d.by_category.map((c) => ({ label: c.category, value: c.count }))} />
          ) : (
            <Empty>Sem reclamações neste ano.</Empty>
          )}
        </Panel>
      </div>

      <Panel
        title="Pedido vs. resolução"
        action={
          d.resolutions.match_pct != null ? (
            <span className="text-xs text-muted-foreground">
              {d.resolutions.match_pct}% das encerradas com pedido receberam o que pediram ({d.resolutions.closed_with_expectation})
            </span>
          ) : null
        }
      >
        {d.resolutions.rows.some((r) => r.expected || r.given) ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[28rem] text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="pb-2 font-medium">Tipo de resolução</th>
                  <th className="pb-2 text-right font-medium">Pedido pelos clientes</th>
                  <th className="pb-2 text-right font-medium">Dado no fecho</th>
                </tr>
              </thead>
              <tbody>
                {d.resolutions.rows.map((r) => (
                  <tr key={r.resolution} className="border-t border-border/50">
                    <td className="py-1.5">{r.resolution}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.expected}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.given}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>Sem pedidos nem resoluções neste ano.</Empty>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Satisfação média por mês" className="lg:col-span-2">
          <MonthColumns
            items={d.by_month.map((m) => ({ label: fmtMonth(m.month), value: m.score_avg }))}
            max={5}
            format={(v) => fmtNum(v)}
          />
        </Panel>
        <Panel title="Requer atenção">
          {d.attention.length ? (
            <ul className="space-y-3">
              {d.attention.map((a, i) => (
                <li key={i}>
                  <button
                    className="w-full text-left disabled:cursor-default"
                    disabled={!a.complaint_id}
                    onClick={() => a.complaint_id && onOpen(a.complaint_id)}
                  >
                    <span
                      className={cn(
                        "text-xs font-semibold",
                        a.kind === "overdue" ? "text-red-600" : a.kind === "due_soon" ? "text-amber-600" : "text-orange-600",
                      )}
                    >
                      {a.kind === "low_score" ? <ThumbsDown className="mr-1 inline h-3 w-3" /> : null}
                      {a.title}
                    </span>
                    <span className="block text-sm">{a.detail}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Nada pendente.</Empty>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Últimas reclamações">
          {d.recent_complaints.length ? (
            <ul className="divide-y divide-border/60">
              {d.recent_complaints.map((c) => (
                <li key={c.id}>
                  <button className="flex w-full items-center justify-between gap-3 py-2 text-left" onClick={() => onOpen(c.id)}>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{c.ref}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {c.submitted_name ?? "—"} · {c.category ?? "Sem motivo"}
                      </span>
                    </span>
                    <StatusBadge c={c} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Sem reclamações.</Empty>
          )}
        </Panel>
        <Panel title="Últimas respostas">
          {d.recent_responses.length ? (
            <ul className="divide-y divide-border/60">
              {d.recent_responses.map((r) => (
                <li key={r.id} className="py-2">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate font-medium">{r.client_label ?? "Cliente"}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {fmtShort(r.submitted_at)} · NPS {r.nps ?? "—"} · {fmtNum(r.score_avg)}
                    </span>
                  </div>
                  {r.comment ? <p className="truncate text-xs text-muted-foreground">“{r.comment}”</p> : null}
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Sem respostas — os envios chegam com os automatismos.</Empty>
          )}
        </Panel>
      </div>
    </>
  )
}
